import { useState } from 'react';
import { Plus, Pencil, X, Trash2, Send, MapPin, ClipboardList, Users as UsersIcon, CheckCircle2, CalendarDays, Share2 } from 'lucide-react';
import WhatsAppDeepLinkButton from '@/components/WhatsAppDeepLinkButton';
import { useMeetings } from '@/contexts/MeetingsContext';
import { toast } from 'sonner';
import { LOCATION_OPTIONS, AGENDA_OPTIONS, formatDateDMY, locationText, agendaText, buildMomSummary } from '@/components/meetingsData';

/*
  Meetings (MOMs) Tab — reads from the shared MeetingsContext (mock/local state).
  Meeting data + Telegram hook live in MeetingsContext so the Design Fee tab can
  read the same on-site meetings. Swap the context handlers for API calls later.
*/

// ─── Main Tab ───────────────────────────────────────────
export default function MeetingsTab({ clientName = '', clientPhone = '', projectName = '', senderName = '' }) {
  const { meetings, saveMeeting, deleteMeeting } = useMeetings();
  const [editing, setEditing] = useState(null); // meeting object (existing) or blank draft
  const [modalOpen, setModalOpen] = useState(false);
  const [sharing, setSharing] = useState(null); // meeting being shared
  const [activeLocationFilter, setActiveLocationFilter] = useState('all'); // 'all'|'on_site'|'office'|'selection'|'other'

  const nextNumber = meetings.reduce((max, m) => Math.max(max, m.number || 0), 0) + 1;

  const openAdd = () => {
    setEditing({
      id: '', number: nextNumber, date: '', location: 'on_site', locationCustom: '',
      agenda: '', agendaCustom: '', attendees: '', decisions: [''], actions: [''], source: 'manual',
    });
    setModalOpen(true);
  };

  const openEdit = (meeting) => {
    setEditing({
      ...meeting,
      decisions: meeting.decisions?.length ? [...meeting.decisions] : [''],
      actions: meeting.actions?.length ? [...meeting.actions] : [''],
    });
    setModalOpen(true);
  };

  const handleSave = async (formData) => {
    try {
      await saveMeeting(formData);
      setModalOpen(false);
      setEditing(null);
      toast.success('Meeting saved');
    } catch {
      toast.error('Failed to save meeting');
    }
  };

  const sorted = [...meetings].sort((a, b) => (a.number || 0) - (b.number || 0));

  // Per-location counts (used to hide empty filter chips) + filtered list
  const locationCounts = sorted.reduce((acc, m) => {
    acc[m.location] = (acc[m.location] || 0) + 1;
    return acc;
  }, {});
  const filtered = activeLocationFilter === 'all'
    ? sorted
    : sorted.filter(m => m.location === activeLocationFilter);

  return (
    <div data-testid="meetings-tab">
      <MeetingListHeader onAdd={openAdd} count={meetings.length} />

      <LocationFilterBar
        active={activeLocationFilter}
        onChange={setActiveLocationFilter}
        counts={locationCounts}
      />

      <div className="space-y-3 mt-4" data-testid="meetings-list">
        {filtered.map(m => (
          <MeetingCard key={m.id} meeting={m} onEdit={() => openEdit(m)} onShare={() => setSharing(m)} />
        ))}

        {meetings.length === 0 && (
          <div className="text-center py-12 border border-dashed border-[#D4D0C8] rounded-xl">
            <ClipboardList size={28} strokeWidth={1.5} className="text-[#D4D0C8] mx-auto mb-2" />
            <p className="text-sm text-[#6B7280]">No meetings recorded yet</p>
            <button onClick={openAdd} className="text-xs text-[#3A8C8C] hover:underline mt-2">Add your first meeting</button>
          </div>
        )}
        {meetings.length > 0 && filtered.length === 0 && (
          <div className="text-center py-12 border border-dashed border-[#D4D0C8] rounded-xl" data-testid="meetings-empty-filter">
            <ClipboardList size={28} strokeWidth={1.5} className="text-[#D4D0C8] mx-auto mb-2" />
            <p className="text-sm text-[#6B7280]">No meetings found for this location.</p>
          </div>
        )}
      </div>

      {modalOpen && editing && (
        <MeetingDetailModal
          meeting={editing}
          onClose={() => { setModalOpen(false); setEditing(null); }}
          onSave={handleSave}
          onDelete={editing.id ? async () => { try { await deleteMeeting(editing.id); setModalOpen(false); setEditing(null); toast.success('Meeting deleted'); } catch { toast.error('Failed to delete'); } } : null}
        />
      )}

      {sharing && (
        <ShareMomModal
          meeting={sharing}
          clientName={clientName}
          clientPhone={clientPhone}
          projectName={projectName}
          senderName={senderName}
          onClose={() => setSharing(null)}
        />
      )}
    </div>
  );
}

// ─── Share MOM Modal (WhatsApp / Telegram deep-link) ────
function ShareMomModal({ meeting, clientName, clientPhone, projectName, senderName, onClose }) {
  const summary = buildMomSummary(meeting, projectName);
  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-4" data-testid="share-mom-modal">
      <div className="absolute inset-0 bg-black/30 backdrop-blur-[2px]" onClick={onClose} />
      <div className="relative w-full max-w-lg bg-[#FAFAF8] rounded-2xl shadow-xl flex flex-col max-h-[90vh] animate-fade-in">
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#E8E6E1] bg-white rounded-t-2xl">
          <h3 className="text-base font-semibold text-[#2C2C2E] font-['Manrope'] flex items-center gap-2">
            <Share2 size={16} className="text-[#3A8C8C]" /> Share Meeting #{meeting.number}
          </h3>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-[#E8E6E1] transition-colors" data-testid="share-mom-close">
            <X size={18} strokeWidth={1.5} className="text-[#6B7280]" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-5">
          <p className="text-xs text-[#6B7280] mb-3">A clean minutes-of-meeting summary is pre-filled below. Send it to the client on WhatsApp, or hit <strong>Copy Text</strong> to paste into Telegram or any chat.</p>
          <WhatsAppDeepLinkButton
            clientName={clientName}
            clientPhone={clientPhone}
            projectName={projectName}
            senderName={senderName}
            template="custom"
            customMessage={summary}
            defaultCountry="91"
          />
        </div>
      </div>
    </div>
  );
}

// ─── List Header ────────────────────────────────────────
function MeetingListHeader({ onAdd, count }) {
  return (
    <div className="flex items-center justify-between flex-wrap gap-2">
      <div>
        <h4 className="section-heading text-sm font-semibold font-['Manrope']">Meetings (MOMs)</h4>
        <p className="text-xs text-[#6B7280] mt-0.5">{count} {count === 1 ? 'meeting' : 'meetings'} recorded</p>
      </div>
      <button onClick={onAdd} data-testid="add-meeting-btn"
        className="flex items-center gap-1.5 px-4 py-1.5 rounded-full bg-[#3A8C8C] text-white text-xs font-medium hover:bg-[#2E7A7A] transition-colors active:scale-95">
        <Plus size={14} strokeWidth={2} /> Add Meeting
      </button>
    </div>
  );
}

// ─── Location Filter Bar ────────────────────────────────
function LocationFilterBar({ active, onChange, counts }) {
  // "All" is always shown; other chips only when they have ≥ 1 meeting
  const chips = [
    { value: 'all', label: 'All' },
    ...LOCATION_OPTIONS.filter(o => (counts[o.value] || 0) > 0),
  ];
  return (
    <div className="flex items-center gap-2 flex-wrap mt-4" data-testid="location-filter-bar">
      {chips.map(c => {
        const isActive = active === c.value;
        return (
          <button key={c.value} onClick={() => onChange(c.value)} data-testid={`location-filter-${c.value}`}
            className={`px-4 py-1.5 rounded-full text-xs font-medium transition-colors border ${
              isActive
                ? 'bg-[#3A8C8C] text-white border-[#3A8C8C]'
                : 'bg-white text-[#3A8C8C] border-[#3A8C8C]/40 hover:border-[#3A8C8C]'
            }`}>
            {c.label}
          </button>
        );
      })}
    </div>
  );
}

// ─── Meeting Card ───────────────────────────────────────
function MeetingCard({ meeting, onEdit, onShare }) {
  const decisionsLine = (meeting.decisions || []).filter(Boolean).join(' · ');
  return (
    <div className="glass-card rounded-xl p-4 group hover:shadow-sm transition-shadow" data-testid={`meeting-card-${meeting.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="section-heading text-sm font-semibold font-['Manrope']">Meeting #{meeting.number}</span>
            <span className="flex items-center gap-1 text-xs text-[#6B7280]">
              <CalendarDays size={12} strokeWidth={1.5} /> {formatDateDMY(meeting.date)}
            </span>
            {meeting.source === 'telegram' && (
              <span title="Added via Telegram bot" data-testid={`meeting-telegram-badge-${meeting.id}`}
                className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-[#229ED9]/10 text-[#229ED9] text-[9px] font-medium">
                <Send size={9} strokeWidth={2} /> Telegram
              </span>
            )}
          </div>
          <div className="flex items-center gap-1.5 mt-2 flex-wrap">
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-[#3A8C8C]/10 text-[#3A8C8C] text-[10px] font-medium">
              <MapPin size={10} strokeWidth={1.5} /> {locationText(meeting)}
            </span>
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-[#E8E6E1] text-[#2C2C2E] text-[10px] font-medium">
              <ClipboardList size={10} strokeWidth={1.5} /> {agendaText(meeting)}
            </span>
          </div>
          {decisionsLine && (
            <p className="text-xs text-[#6B7280] mt-2 truncate" title={decisionsLine}>
              <span className="text-[#2C2C2E] font-medium">Decisions:</span> {decisionsLine}
            </p>
          )}
        </div>
        <div className="shrink-0 flex items-center gap-0.5">
          <button onClick={onShare} data-testid={`meeting-share-btn-${meeting.id}`}
            className="p-1.5 rounded-lg text-[#3A8C8C] hover:bg-[#3A8C8C]/10 transition-colors"
            title="Share MOM via WhatsApp / Telegram">
            <Share2 size={15} strokeWidth={1.5} />
          </button>
          <button onClick={onEdit} data-testid={`meeting-edit-btn-${meeting.id}`}
            className="p-1.5 rounded-lg text-[#3A8C8C] hover:bg-[#3A8C8C]/10 transition-colors"
            title="Edit meeting">
            <Pencil size={15} strokeWidth={1.5} />
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Detail Modal (Add / Edit) ──────────────────────────
function MeetingDetailModal({ meeting, onClose, onSave, onDelete }) {
  const [form, setForm] = useState(meeting);
  const [error, setError] = useState('');

  const set = (patch) => setForm(prev => ({ ...prev, ...patch }));

  const handleSave = () => {
    if (!form.date || !form.agenda) {
      setError('Date and Agenda are required.');
      return;
    }
    const cleaned = {
      ...form,
      decisions: (form.decisions || []).map(d => d.trim()).filter(Boolean),
      actions: (form.actions || []).map(a => a.trim()).filter(Boolean),
      locationCustom: (form.location === 'selection' || form.location === 'other') ? (form.locationCustom || '') : '',
      agendaCustom: (form.agenda === 'selection' || form.agenda === 'other') ? (form.agendaCustom || '') : '',
    };
    onSave(cleaned);
  };

  return (
    <div className="fixed inset-0 z-[200] flex justify-end" data-testid="meeting-detail-modal">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/30 backdrop-blur-[2px]" onClick={onClose} />
      {/* Slide-over panel — full-screen on mobile, right panel on desktop */}
      <div className="relative w-full sm:max-w-lg h-full bg-[#FAFAF8] shadow-xl flex flex-col animate-slide-in-right">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#E8E6E1] bg-white">
          <div className="flex items-center gap-2">
            <h3 className="text-base font-semibold text-[#2C2C2E] font-['Manrope']">
              {meeting.id ? `Edit Meeting #${meeting.number}` : `New Meeting #${meeting.number}`}
            </h3>
            {form.source === 'telegram' && (
              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-[#229ED9]/10 text-[#229ED9] text-[9px] font-medium">
                <Send size={9} strokeWidth={2} /> Telegram
              </span>
            )}
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-[#E8E6E1] transition-colors" data-testid="meeting-modal-close">
            <X size={18} strokeWidth={1.5} className="text-[#6B7280]" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-5 py-5 space-y-6">
          {/* Section: Meeting Info */}
          <Section icon={CalendarDays} title="Meeting Info">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <FieldLabel>Date *</FieldLabel>
                <input type="date" value={form.date} onChange={e => set({ date: e.target.value })}
                  className="w-full h-9 px-3 rounded-lg border border-[#D4D0C8] text-sm bg-white focus:outline-none focus:border-[#3A8C8C]"
                  data-testid="meeting-date-input" />
              </div>
              <LocationDropdown value={form.location} custom={form.locationCustom}
                onValue={v => set({ location: v })} onCustom={v => set({ locationCustom: v })} />
              <AgendaDropdown value={form.agenda} custom={form.agendaCustom}
                onValue={v => set({ agenda: v })} onCustom={v => set({ agendaCustom: v })} />
            </div>
          </Section>

          {/* Section: Attendees */}
          <Section icon={UsersIcon} title="Attendees">
            <AttendeesInput value={form.attendees} onChange={v => set({ attendees: v })} />
          </Section>

          {/* Section: Decisions */}
          <Section icon={CheckCircle2} title="Decisions">
            <DecisionsInput items={form.decisions} onChange={items => set({ decisions: items })} />
          </Section>

          {/* Section: Actions */}
          <Section icon={ClipboardList} title="Actions">
            <ActionsInput items={form.actions} onChange={items => set({ actions: items })} />
          </Section>
        </div>

        {/* Footer */}
        <div className="px-5 py-4 border-t border-[#E8E6E1] bg-white">
          {error && <p className="text-xs text-[#C4727F] mb-2" data-testid="meeting-form-error">{error}</p>}
          <div className="flex items-center gap-2">
            {onDelete && (
              <button onClick={onDelete} data-testid="meeting-delete-btn"
                className="flex items-center gap-1.5 px-3 h-10 rounded-full border border-[#C4727F]/40 text-[#C4727F] text-sm font-medium hover:bg-[#C4727F]/10 transition-colors">
                <Trash2 size={14} /> Delete
              </button>
            )}
            <div className="flex-1" />
            <button onClick={onClose}
              className="px-4 h-10 rounded-full border border-[#D4D0C8] text-sm font-medium text-[#6B7280] hover:bg-[#E8E6E1] transition-colors">
              Cancel
            </button>
            <button onClick={handleSave} data-testid="meeting-save-btn"
              className="px-5 h-10 rounded-full bg-[#3A8C8C] text-white text-sm font-medium hover:bg-[#2E7A7A] transition-colors active:scale-95">
              Save Meeting
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Shared bits ────────────────────────────────────────
function Section({ icon: Icon, title, children }) {
  return (
    <div>
      <div className="flex items-center gap-1.5 mb-2.5">
        {Icon && <Icon size={14} strokeWidth={1.5} className="text-[#3A8C8C]" />}
        <h4 className="text-xs font-semibold text-[#2C2C2E] uppercase tracking-wider font-['Manrope']">{title}</h4>
      </div>
      {children}
    </div>
  );
}
function FieldLabel({ children }) {
  return <label className="block text-[10px] text-[#6B7280] uppercase tracking-wide mb-1">{children}</label>;
}

function LocationDropdown({ value, custom, onValue, onCustom }) {
  const showCustom = value === 'selection' || value === 'other';
  return (
    <div>
      <FieldLabel>Location</FieldLabel>
      <select value={value} onChange={e => onValue(e.target.value)}
        className="w-full h-9 px-2 rounded-lg border border-[#D4D0C8] text-sm bg-white focus:outline-none focus:border-[#3A8C8C]"
        data-testid="meeting-location-select">
        {LOCATION_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
      {showCustom && (
        <input value={custom} onChange={e => onCustom(e.target.value)} placeholder="Specify location…"
          className="w-full h-9 px-3 mt-1.5 rounded-lg border border-[#D4D0C8] text-sm bg-white focus:outline-none focus:border-[#3A8C8C]"
          data-testid="meeting-location-custom" />
      )}
    </div>
  );
}

function AgendaDropdown({ value, custom, onValue, onCustom }) {
  const showCustom = value === 'selection' || value === 'other';
  return (
    <div>
      <FieldLabel>Agenda *</FieldLabel>
      <select value={value} onChange={e => onValue(e.target.value)}
        className="w-full h-9 px-2 rounded-lg border border-[#D4D0C8] text-sm bg-white focus:outline-none focus:border-[#3A8C8C]"
        data-testid="meeting-agenda-select">
        <option value="">Select…</option>
        {AGENDA_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
      {showCustom && (
        <input value={custom} onChange={e => onCustom(e.target.value)} placeholder="Specify agenda…"
          className="w-full h-9 px-3 mt-1.5 rounded-lg border border-[#D4D0C8] text-sm bg-white focus:outline-none focus:border-[#3A8C8C]"
          data-testid="meeting-agenda-custom" />
      )}
    </div>
  );
}

function AttendeesInput({ value, onChange }) {
  return (
    <div>
      <textarea value={value} onChange={e => onChange(e.target.value)} rows={2}
        placeholder="Comma-separated names, e.g. Juthika, Client, Site Engineer"
        className="w-full px-3 py-2 rounded-lg border border-[#D4D0C8] text-sm bg-white resize-none focus:outline-none focus:border-[#3A8C8C]"
        data-testid="meeting-attendees-input" />
      {value?.trim() && (
        <div className="flex flex-wrap gap-1 mt-1.5">
          {value.split(',').map(n => n.trim()).filter(Boolean).map((n, i) => (
            <span key={i} className="px-2 py-0.5 rounded-md bg-[#E8E6E1] text-[#2C2C2E] text-[10px]">{n}</span>
          ))}
        </div>
      )}
    </div>
  );
}

function DecisionsInput({ items, onChange }) {
  const update = (i, val) => onChange(items.map((d, idx) => idx === i ? val : d));
  const add = () => onChange([...items, '']);
  const remove = (i) => onChange(items.filter((_, idx) => idx !== i));
  return (
    <div className="space-y-2" data-testid="meeting-decisions">
      {items.map((d, i) => (
        <div key={i} className="flex items-center gap-2">
          <span className="text-[#3A8C8C] text-xs mt-0.5">{i + 1}.</span>
          <input value={d} onChange={e => update(i, e.target.value)} placeholder="Decision taken…"
            className="flex-1 h-9 px-3 rounded-lg border border-[#D4D0C8] text-sm bg-white focus:outline-none focus:border-[#3A8C8C]"
            data-testid={`meeting-decision-input-${i}`} />
          <button onClick={() => remove(i)} className="p-1.5 rounded-lg text-[#C4727F] hover:bg-[#C4727F]/10 transition-colors" title="Remove">
            <X size={14} />
          </button>
        </div>
      ))}
      <button onClick={add} data-testid="meeting-add-decision"
        className="flex items-center gap-1.5 text-xs text-[#3A8C8C] font-medium hover:underline">
        <Plus size={13} /> Add Decision
      </button>
    </div>
  );
}

function ActionsInput({ items, onChange }) {
  const update = (i, val) => onChange(items.map((a, idx) => idx === i ? val : a));
  const add = () => onChange([...items, '']);
  const remove = (i) => onChange(items.filter((_, idx) => idx !== i));
  return (
    <div className="space-y-2" data-testid="meeting-actions">
      {items.map((a, i) => (
        <div key={i} className="flex items-center gap-2">
          <span className="text-[#3A8C8C] mt-0.5">•</span>
          <input value={a} onChange={e => update(i, e.target.value)} placeholder="Action item…"
            className="flex-1 h-9 px-3 rounded-lg border border-[#D4D0C8] text-sm bg-white focus:outline-none focus:border-[#3A8C8C]"
            data-testid={`meeting-action-input-${i}`} />
          <button onClick={() => remove(i)} className="p-1.5 rounded-lg text-[#C4727F] hover:bg-[#C4727F]/10 transition-colors" title="Remove">
            <X size={14} />
          </button>
        </div>
      ))}
      <button onClick={add} data-testid="meeting-add-action"
        className="flex items-center gap-1.5 text-xs text-[#3A8C8C] font-medium hover:underline">
        <Plus size={13} /> Add Action
      </button>
    </div>
  );
}
